import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { useApp } from '../../context/AppContext';
import api from '../../services/api';

import styles from './CircularMenu.module.css';
import CircularMenuText from './CircularMenuText';

/**
 * Check if current content is a blog page (needs fixed menu)
 */
const useBlogMode = () => {
  const { content } = useApp();
  return content?.type === 'blog';
};

/**
 * CircularMenu Component
 *
 * Renders the central navigation menu circle with:
 * - Artist photo in the center (circular crop)
 * - Menu items positioned around the edge using polar coordinates
 * - Glowing pink ring effect
 * - Active state tracking
 * - Smooth animations
 */
const CircularMenu: React.FC = () => {
  const { menuItems, loadMenuItems, activeMenuItem, setActiveMenuItem, isLoadingMenu, menuError } =
    useApp();
  const isBlogMode = useBlogMode();

  const [artistPhotoUrl, setArtistPhotoUrl] = useState<string>('');
  const [artistName, setArtistName] = useState<string>('MD');

  // Load menu items on mount
  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  // Load site settings (artist photo) on mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await api.siteSettings.get();
        if (settings.artistPhotoUrl) {
          setArtistPhotoUrl(settings.artistPhotoUrl);
        }
        if (settings.artistName) {
          setArtistName(settings.artistName);
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  /**
   * Handle menu item click
   * @param slug
   */
  const handleMenuItemClick = (slug: string): void => {
    setActiveMenuItem(slug);
  };

  // Loading state
  if (isLoadingMenu) {
    return (
      <div className={styles.circularMenuContainer}>
        <div className={styles.circularMenu}>
          <div className={styles.loading}>Loading menu...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (menuError) {
    return (
      <div className={styles.circularMenuContainer}>
        <div className={styles.circularMenu}>
          <div className={styles.error}>Error: {menuError}</div>
        </div>
      </div>
    );
  }

  // Empty menu state
  if (menuItems.length === 0) {
    return (
      <div className={styles.circularMenuContainer}>
        <div className={styles.circularMenu}>
          <div className={styles.empty}>No menu items available</div>
        </div>
      </div>
    );
  }

  const containerClasses = `${styles.circularMenuContainer} ${isBlogMode ? styles.fixed : ''}`;

  return (
    <div className={containerClasses}>
      <motion.div
        className={styles.circularMenu}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Artist photo in center */}
        <div className={styles.centerPhoto}>
          {artistPhotoUrl ? (
            <img
              src={artistPhotoUrl}
              alt={`${artistName} - Artist Photo`}
              className={styles.artistPhoto}
            />
          ) : (
            <div className={styles.photoPlaceholder}>{artistName}</div>
          )}
        </div>

        {/* Menu text wrapping around the circular border */}
        <CircularMenuText
          menuItems={menuItems}
          radius={130}
          activeMenuItem={activeMenuItem}
          onMenuItemClick={handleMenuItemClick}
        />
      </motion.div>
    </div>
  );
};

export default CircularMenu;
